import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { formatCurrency, getImageUrl } from "../../../core/api";
import { deleteOrder as deleteOrderRequest, getOrders, updateOrder, updateOrderAction } from "../../../core/services/orderService";
import AppImage from "../../../shared/AppImage";
import { paymentMethodLabel, promptTrackingDetails, trackingFromOrder } from "../../../shared/Utils";
import MetricCard from "../../../shared/MetricCard";
import PaginationBar from "../../../shared/PaginationBar";

const ORDER_STATUSES = [
  "Processing",
  "Shipped",
  "Delivered",
  "Cancelled",
  "Return Requested",
  "Return Approved",
  "Return Rejected",
  "Refunded",
];

const getAvailableStatusOptions = (status?: string) => {
  const options: Record<string, string[]> = {
    Processing: ["Processing", "Shipped", "Cancelled"],
    Shipped: ["Shipped", "Delivered", "Cancelled"],
    Delivered: ["Delivered", "Return Requested"],
    "Return Requested": [
      "Return Requested",
      "Return Approved",
      "Return Rejected",
    ],
    "Return Approved": ["Return Approved", "Refunded"],
    Cancelled: ["Cancelled", "Refunded"],
  };

  return options[status || ""] || [status || "-"];
};

const formatDate = (date?: string) => {
  if (!date) return "-";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(date));
};

const statusTone = (status?: string) => {
  if (["Delivered", "Refunded", "Return Approved"].includes(status || "")) {
    return "success";
  }

  if (["Processing", "Shipped", "Return Requested"].includes(status || "")) {
    return "warning";
  }

  if (["Cancelled", "Return Rejected"].includes(status || "")) {
    return "danger";
  }

  return "muted";
};

const OrdersList = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalOrders: 0,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    payment: "all",
    sort: "newest",
  });

  const fetchOrders = useCallback(
    async (page = 1) => {
      setIsLoading(true);

      try {
        const params = new URLSearchParams({
          search: filters.search,
          status: filters.status,
          payment: filters.payment,
          sort: filters.sort,
          page: String(page),
          limit: String(pagination.limit),
        });

        const result = await getOrders(params.toString());

        if (result.success) {
          setOrders(result.orders || []);
          setTotalAmount(result.totalAmount || 0);
          if (result.pagination) {
            setPagination({
              page: result.pagination.page,
              limit: result.pagination.limit,
              totalPages: result.pagination.totalPages,
              totalOrders: result.totalOrders || (result.orders || []).length,
              total: result.pagination.total ?? result.totalOrders ?? (result.orders || []).length,
            });
          }
        }
      } catch (error: any) {
        Swal.fire({
          title: error.message || "Unable to load orders",
          icon: "error",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [filters, pagination.limit],
  );

  useEffect(() => {
    fetchOrders(1);
  }, [fetchOrders]);

  const currentOrders = orders;

  const resetFilters = () => {
    setFilters({
      search: "",
      status: "all",
      payment: "all",
      sort: "newest",
    });
    setPagination((old) => ({ ...old, page: 1 }));
  };

  const updateOrderStatus = async (order: any, status: string) => {
    if (order.orderStatus === status) return;

    if (status === "Cancelled") {
      await cancelOrder(order);
      return;
    }

    if (status === "Return Approved" || status === "Return Rejected") {
      await processReturn(order, status === "Return Approved" ? "approve" : "reject");
      return;
    }

    if (status === "Refunded") {
      await refundOrder(order);
      return;
    }

    let reason = "";
    let trackingPayload = {};
    if (status === "Return Requested") {
      const response = await Swal.fire({
        title: "Request return?",
        input: "textarea",
        inputLabel: "Return reason",
        inputPlaceholder: "Write reason...",
        showCancelButton: true,
        confirmButtonText: "Request Return",
      });

      if (!response.isConfirmed) return;
      reason = response.value || "Requested by admin";
    }

    if (status === "Shipped" && !order.trackingInfo?.trackingNumber) {
      const trackingResult = await promptTrackingDetails(trackingFromOrder(order));
      if (trackingResult === null) return;
      if (trackingResult !== "skipped") {
        trackingPayload = trackingResult;
      }
    }

    try {
      const result = await updateOrder(order._id, {
        status,
        ...(reason && { reason }),
        ...trackingPayload,
      });

      if (!result.success) {
        throw new Error(result.message || "Status update failed");
      }

      if (
        status === "Delivered" &&
        (result.order?.paymentInfo?.method || order.paymentInfo?.method) === "cod" &&
        (result.order?.paymentInfo?.status || order.paymentInfo?.status) === "Pending"
      ) {
        const collect = await Swal.fire({
          title: "COD cash collected?",
          text: "Mark this order payment as Completed now?",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Mark Collected",
          cancelButtonText: "Keep Pending",
        });

        if (collect.isConfirmed) {
          await updateOrderAction(order._id, "payment", {
            status: "Completed",
          });
        }
      }

      await fetchOrders();
      Swal.fire({
        title: "Order Updated",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire("Update Failed", error.message || "Status update failed", "error");
    }
  };

  const cancelOrder = async (order: any) => {
    const response = await Swal.fire({
      title: "Cancel order?",
      input: "textarea",
      inputLabel: "Cancellation reason",
      inputPlaceholder: "Write reason...",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cancel Order",
      confirmButtonColor: "#ef4444",
    });

    if (!response.isConfirmed) return;

    try {
      const result = await updateOrderAction(order._id, "cancel", {
        reason: response.value || "Cancelled by admin",
      });

      if (!result.success) {
        throw new Error(result.message || "Order cancel failed");
      }

      await fetchOrders();
      Swal.fire({
        title: "Order Cancelled",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire("Cancel Failed", error.message || "Order cancel failed", "error");
    }
  };

  const processReturn = async (order: any, action: "approve" | "reject") => {
    const response = await Swal.fire({
      title: action === "approve" ? "Approve return?" : "Reject return?",
      input: "textarea",
      inputLabel: "Admin note",
      inputPlaceholder: "Write note...",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: action === "approve" ? "Approve" : "Reject",
    });

    if (!response.isConfirmed) return;

    try {
      const result = await updateOrderAction(order._id, "return", {
        action,
        adminNote: response.value || "",
      });

      if (!result.success) {
        throw new Error(result.message || "Return update failed");
      }

      await fetchOrders();
      Swal.fire({
        title: "Return Updated",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire("Return Failed", error.message || "Return update failed", "error");
    }
  };

  const refundOrder = async (order: any) => {
    const response = await Swal.fire({
      title: "Process refund?",
      html: `<p style="margin:0 0 12px;color:#64748b">Default refund amount: ${formatCurrency(order.refundInfo?.amount || order.totalPrice)}</p>`,
      input: "number",
      inputLabel: "Refund amount",
      inputValue: order.refundInfo?.amount || order.totalPrice,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Refund",
    });

    if (!response.isConfirmed) return;

    try {
      const result = await updateOrderAction(order._id, "refund", {
        status: "Completed",
        amount: Number(response.value || order.totalPrice),
      });

      if (!result.success) {
        throw new Error(result.message || "Refund update failed");
      }

      await fetchOrders();
      Swal.fire({
        title: "Refund Updated",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire("Refund Failed", error.message || "Refund update failed", "error");
    }
  };

  const deleteOrder = async (order: any) => {
    const confirm = await Swal.fire({
      title: "Delete order?",
      text: "This order will be removed permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete",
    });

    if (!confirm.isConfirmed) return;

    try {
      const result = await deleteOrderRequest(order._id);

      if (!result.success) {
        throw new Error(result.message || "Order delete failed");
      }

      await fetchOrders();
      Swal.fire({
        title: "Order Deleted",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire("Delete Failed", error.message || "Order delete failed", "error");
    }
  };

  const metrics = [
    {
      label: "Total Orders",
      value: orders.length,
      icon: "📦",
      tone: "orange",
    },
    {
      label: "Processing Orders",
      value: orders.filter((item) => item.orderStatus === "Processing").length,
      icon: "🧾",
      tone: "blue",
    },
    {
      label: "Cancelled Orders",
      value: orders.filter((item) => item.orderStatus === "Cancelled").length,
      icon: "⛔",
      tone: "purple",
    },
    {
      label: "Total Revenue",
      value: formatCurrency(totalAmount),
      icon: "💰",
      tone: "pink",
    },
  ];

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">Overview</div>
          <h1 className="page-title">
            All <em>Orders</em>
          </h1>
          <div className="page-sub">
            Track all orders, payment status, delivery status and refunds.
          </div>
        </div>
      </div>

      <section className="metrics-grid">
        {metrics.map((metric) => (
          <MetricCard key={metric.label} metric={metric} />
        ))}
      </section>

      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={filters.search}
            onChange={(e) => {
              setFilters({ ...filters, search: e.target.value });
              setPagination((old) => ({ ...old, page: 1 }));
            }}
            placeholder="Search order, AWB, customer, phone..."
          />
        </div>

        <select
          className="filter-select"
          value={filters.status}
          onChange={(e) => {
            setFilters({ ...filters, status: e.target.value });
            setPagination((old) => ({ ...old, page: 1 }));
          }}
        >
          <option value="all">All Order Status</option>
          {ORDER_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filters.payment}
          onChange={(e) => {
            setFilters({ ...filters, payment: e.target.value });
            setPagination((old) => ({ ...old, page: 1 }));
          }}
        >
          <option value="all">All Payments</option>
          <option value="Completed">Completed</option>
          <option value="Pending">Pending</option>
          <option value="Failed">Failed</option>
        </select>

        <select
          className="filter-select"
          value={filters.sort}
          onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="amountHigh">Amount High</option>
          <option value="amountLow">Amount Low</option>
        </select>

        <div className="toolbar-right">
          <button type="button" className="btn-sm btn-outline" onClick={resetFilters}>
            <svg viewBox="0 0 24 24">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 101.85-6.86L1 10" />
            </svg>
            Reset
          </button>
        </div>
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>Orders List</h2>
          <p>Manage Orders</p>
        </div>

        <div className="table-scroll">
          <table>
          <thead>
            <tr>
              <th>Order</th>
              <th>Customer</th>
              <th>Items</th>
              <th>Payment Status</th>
              <th>Total</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="empty-state">
                  Loading Orders...
                </td>
              </tr>
            ) : currentOrders.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-state">
                  No Orders found.
                </td>
              </tr>
            ) : (
              currentOrders.map((order) => {
                const firstItem = order.orderItems?.[0];
                const statusOptions = getAvailableStatusOptions(order.orderStatus);
                const canUpdate = statusOptions.length > 1;
                const canCancel = ![
                  "Delivered",
                  "Cancelled",
                  "Refunded",
                ].includes(order.orderStatus);
                const canProcessReturn =
                  order.orderStatus === "Return Requested" &&
                  order.returnRequest?.status === "Requested";
                const canRefund = ["Cancelled", "Return Approved"].includes(
                  order.orderStatus,
                );

                return (
                  <tr key={order._id}>
                    <td>
                      <Link
                        to={`/orders/${order._id}`}
                        className="table-link"
                        title="View Order Details"
                      >
                        {order.orderCode || "-"}
                      </Link>
                      <small className="table-muted">
                        {paymentMethodLabel(order.paymentInfo?.method)}
                      </small>
                      {order.trackingInfo?.trackingNumber ? (
                        <small className="table-muted">
                          {order.trackingInfo.courierName || "Courier"} ·{" "}
                          {order.trackingInfo.trackingNumber}
                        </small>
                      ) : null}
                    </td>
                    <td>
                      <strong>
                        {order.user?.name ||
                          order.shippingInfo?.fullName ||
                          "Customer"}
                      </strong>
                      <small className="table-muted">
                        {order.user?.email || order.shippingInfo?.phoneNo || "-"}
                      </small>
                    </td>
                    <td>
                      <div className="img-name-cell">
                        <AppImage
                          src={getImageUrl(firstItem?.image)}
                          alt={firstItem?.name || "Order item"}
                          crossOrigin="anonymous"
                        />
                        <div>
                          <strong>{firstItem?.name || "Order Items"}</strong>
                          <small>
                            {(order.orderItems || []).length} item(s), Qty{" "}
                            {(order.orderItems || []).reduce(
                              (sum: number, item: any) =>
                                sum + Number(item.quantity || 0),
                              0,
                            )}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${
                          order.paymentInfo?.status === "Completed"
                            ? "success"
                            : order.paymentInfo?.status === "Failed"
                              ? "danger"
                              : "warning"
                        }`}
                      >
                        {order.paymentInfo?.status || "-"}
                      </span>
                    </td>
                    <td>
                      <strong>{formatCurrency(order.totalPrice)}</strong>
                      {Number(order.coupon?.discountAmount || 0) > 0 && (
                        <small className="table-muted">
                          Coupon {formatCurrency(order.coupon.discountAmount)}
                        </small>
                      )}
                    </td>
                    <td>
                      {canUpdate ? (
                        <select
                          className="table-status-select"
                          value={order.orderStatus}
                          onChange={(event) =>
                            updateOrderStatus(order, event.target.value)
                          }
                        >
                          {statusOptions.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`status-badge ${statusTone(
                            order.orderStatus,
                          )}`}
                        >
                          {order.orderStatus}
                        </span>
                      )}
                    </td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>
                      <div className="actions-btns order-actions">
                        <Link
                          to={`/orders/${order._id}`}
                          title="View Details"
                          className="action-button edit"
                        >
                          👁️
                        </Link>

                        {canCancel && (
                          <button
                            type="button"
                            onClick={() => cancelOrder(order)}
                            title="Cancel order"
                            className="action-button delete"
                          >
                            ✖
                          </button>
                        )}

                        {canProcessReturn && (
                          <>
                            <button
                              type="button"
                              onClick={() => processReturn(order, "approve")}
                              title="Approve return"
                              className="action-button edit"
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              onClick={() => processReturn(order, "reject")}
                              title="Reject return"
                              className="action-button delete"
                            >
                              ↯
                            </button>
                          </>
                        )}

                        {canRefund && (
                          <button
                            type="button"
                            onClick={() => refundOrder(order)}
                            title="Refund order"
                            className="action-button edit"
                          >
                            ₹
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => deleteOrder(order)}
                          title="Delete order"
                          className="action-button delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          </table>
        </div>

        <PaginationBar
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total || pagination.totalOrders}
          totalPages={pagination.totalPages}
          itemLabel="orders"
          onPageChange={fetchOrders}
          onLimitChange={(limit) =>
            setPagination((prev) => ({
              ...prev,
              page: 1,
              limit,
            }))
          }
        />
      </section>
    </>
  );
};

export default OrdersList;
